import { Component, Input, OnDestroy } from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  NG_VALIDATORS,
  AbstractControl,
  ValidationErrors,
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
  ValidatorFn,
} from '@angular/forms';
import { Subject, takeUntil, distinctUntilChanged } from 'rxjs';
import { CommonModule } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatError } from '@angular/material/form-field';
import { createISODuration, parseISODuration } from 'utils';

@Component({
  selector: 'ctx-filter-subscription-interval',
  templateUrl: './filter-subscription-interval.component.html',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatError,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: FilterSubscriptionIntervalComponent,
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: FilterSubscriptionIntervalComponent,
      multi: true,
    },
  ],
})
export class FilterSubscriptionIntervalComponent
  implements ControlValueAccessor, OnDestroy
{
  @Input()
  disabled = false;

  durationForm: FormGroup;
  isoString = '';

  private destroy$ = new Subject<void>();

  // eslint-disable-next-line @typescript-eslint/no-unused-vars, @typescript-eslint/no-empty-function
  private onChange = (value: string) => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onTouched = () => {};

  constructor(private fb: FormBuilder) {
    this.durationForm = this.fb.group(
      {
        years: [null, [Validators.required, Validators.min(0)]],
        months: [null, [Validators.required, Validators.min(0)]],
        days: [null, [Validators.required, Validators.min(0)]],
      },
      {
        validators: [this.validateInterval],
      }
    );

    this.durationForm.valueChanges
      .pipe(takeUntil(this.destroy$), distinctUntilChanged())
      .subscribe((value) => {
        this.isoString = createISODuration(
          value.years,
          value.months,
          value.days,
          0,
          0,
          0
        );

        this.onChange(this.isoString);
      });
  }
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
  validateInterval: ValidatorFn = (control: AbstractControl) => {
    const years = control.get('years');
    const months = control.get('months');
    const days = control.get('days');

    if (!years?.value && !months?.value && !days?.value) {
      years?.setValidators([Validators.required, Validators.min(1)]);
      months?.setValidators([Validators.required, Validators.min(1)]);
      days?.setValidators([Validators.required, Validators.min(1)]);
    } else {
      years?.setValidators([Validators.min(0)]);
      months?.setValidators([Validators.min(0)]);
      days?.setValidators([Validators.min(0)]);
    }
    years?.updateValueAndValidity({ onlySelf: true });
    months?.updateValueAndValidity({ onlySelf: true });
    days?.updateValueAndValidity({ onlySelf: true });

    return null;
  };
  // ControlValueAccessor implementation
  writeValue(value: string): void {
    if (value) {
      const duration = parseISODuration(value);
      this.durationForm.patchValue(duration, { emitEvent: false });
      this.isoString = value;
    } else {
      this.durationForm.reset({ years: null, months: null, days: null });
      this.isoString = '';
    }
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    if (isDisabled) {
      this.durationForm.disable();
    } else {
      this.durationForm.enable();
    }
  }
  validate(): ValidationErrors | null {
    const value = this.durationForm.value;
    if (!value) {return null;}

    const { years, months, days } = value;
    if (!years && !months && !days) {
      return { atLeastOneRequired: true };
    }

    // Check if at least one field has a value > 0
    if (years < 0 || months < 0 || days < 0) {
      return { min: true };
    }

    this.durationForm.markAllAsTouched();

    return null;
  }

  onBlur(): void {
    this.onTouched();
  }
}
